/**
 * Who a report goes to, rendered as a name and an avatar (POS2-1137, decision 7).
 *
 * There is no `public.users` table and no avatar, photo or colour column anywhere
 * (`notification_digest.py:66-68`), so identity here is derived from what a recipient row actually
 * carries: an email, and a `user_id` when the recipient is a team member rather than an outside
 * address. Everything below follows from that.
 *
 * The name is never blank and never "?": an address always has a local part, and a local part
 * always has a first letter.
 *
 * The role is deliberately *not* shown. The mock puts a job title on every chip — Head Chef, Sous
 * Chef, General Manager — and the seeded roles are Org Admin, Site Manager and Staff, so a real
 * list would read "Staff · Staff · Staff · Staff", which is worse than nothing (findings F15). Job
 * titles on membership are filed separately.
 */

/** Six tints, all from the DESIGN_PROMPT palette. An outside address gets the neutral one. */
const TINTS = [
  "bg-blue-50 text-blue-700",
  "bg-emerald-50 text-emerald-700",
  "bg-orange-50 text-orange-700",
  "bg-violet-50 text-violet-700",
  "bg-teal-50 text-teal-700",
  "bg-rose-50 text-rose-700",
];

const EXTERNAL_TINT = "bg-gray-100 text-gray-600";

/** The email's local part, split on the separators people actually use, and title-cased. */
export function displayName(email: string): string {
  const local = email.split("@")[0];
  if (!local) return email;
  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length === 0) return email;
  return words.map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

/** The first name alone — what a card says when it has room for one or two people. */
export function firstName(email: string): string {
  return displayName(email).split(" ")[0];
}

/** First letters of the first two words, so one word gives one letter. */
export function initials(email: string): string {
  return displayName(email)
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

/**
 * A stable tint for a recipient, hashed on the **lowercased address**.
 *
 * Decision 7 says `user_id`, and the address is the better key for the reason
 * `20260825063930`'s own comment gives: the email is the durable identity here, not the user id —
 * a delivery record has to outlive the user row. Once history exists that stops being a detail.
 * `report_deliveries` carries the address as sent and no user id at all, so hashing on `user_id`
 * would give the same colleague one colour on their card and another in the send that reached
 * them, on the same screen. The goal decision 7 states — the same person, the same colour
 * everywhere — is only reachable through the field both surfaces have.
 *
 * `external` is the tag rather than the absence of an id, so a history row, which knows neither,
 * simply gets a colour.
 */
export function avatarTint(email: string, external = false): string {
  if (external) return EXTERNAL_TINT;
  let hash = 0;
  for (const char of email.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) % 100_000;
  return TINTS[hash % TINTS.length];
}
